/**
 * Every DISCRETE point the cursor can be caught by, for the snap-candidate dots
 * (#367).
 *
 * The dots exist to answer "where can this land, and which toggle catches it",
 * so the set they mark has to be the set that actually fires. That is harder
 * than it looks, because two different gatherers feed the two snap paths:
 *
 *  - the leather draw tools resolve through `gatherDrawSnapTargets` /
 *    `resolveDrawSnap` — outline + cut-out + seam vertices, hole centres,
 *    construction anchors, then crossings, then a projection onto an edge;
 *  - construction resolves through `findNearestSnapCandidate`, which offers all
 *    of the anchors PLUS three kinds the draw path has no notion of: straight
 *    SEGMENT MIDPOINTS (#377), a closed shape's bbox CENTRE (#417), and its four
 *    bbox EDGE MIDPOINTS.
 *
 * Marking only the first list — which is what shipped — left a user staring at
 * corners while the midpoint they were about to snap to went unmarked. This
 * module unions both, so a dot means the same thing in either tool.
 *
 * Two snap kinds contribute NO dots, by their nature rather than by omission:
 * the line tier projects onto the nearest point ALONG an edge, and the grid
 * tier onto a lattice. Both are continuous — every point of an edge is a
 * candidate — so there is no finite set to speckle, and drawing one would
 * misrepresent them as a handful of discrete targets.
 */
import type paper from "paper";
import type { Project, Vec2 } from "../model/Project";
import { projectConstructionLines } from "../model/Project";
import {
  BBOX_EDGES,
  getBboxCenter,
  getBboxMidpoint,
} from "../model/constraintEnforcement";
import { edgeIntersections } from "../model/edgeIntersections";
import { gatherDrawSnapTargets } from "./drawSnap";

/** Which snap tiers are armed right now. Each contributes its own points, so a
 *  dot always belongs to a toggle that is actually on. */
export interface ActiveSnapKinds {
  /** Point snap — anchors, hole centres, segment midpoints, shape centres and
   *  bbox edge midpoints. */
  point: boolean;
  /** Line snap — the CROSSINGS only (see the module note on why the projection
   *  itself has no dots). */
  line: boolean;
}

/** Positions closer together than this are the same target as far as the eye is
 *  concerned — and often literally are, e.g. a rectangle's bbox edge midpoint
 *  when a polyline vertex already sits there. Matches `edgeIntersections`. */
const DEDUPE_MM = 1e-3;

function dedupe(points: Vec2[]): Vec2[] {
  const seen = new Set<string>();
  const out: Vec2[] = [];
  for (const p of points) {
    const key = `${Math.round(p.x / DEDUPE_MM)}:${Math.round(p.y / DEDUPE_MM)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * The construction-only point kinds, mirroring `findNearestSnapCandidate` in
 * [constraintEnforcement.ts] so the dots and the snap can't disagree. Hidden
 * lines are skipped, exactly as that function skips them.
 */
function constructionExtraPoints(
  project: Project,
  scope: paper.PaperScope,
): Vec2[] {
  const out: Vec2[] = [];
  for (const line of projectConstructionLines(project)) {
    if (line.visible === false) continue;

    // Segment midpoints are offered for a single line and for each STRAIGHT
    // segment of a polyline / polygon. Primitives are skipped: a rect / ellipse
    // / pill gets its edge midpoints from the bbox below instead, and an
    // ellipse's "segments" are curved arcs whose chord midpoint is off-path.
    if (!line.primitive || line.primitive.kind === "line") {
      let cp: paper.CompoundPath | null = null;
      try {
        cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
      } catch {
        cp = null;
      }
      if (cp) {
        const subs =
          cp.children && cp.children.length > 0
            ? (cp.children as paper.Path[])
            : [cp as unknown as paper.Path];
        for (const sub of subs) {
          if (!sub.segments) continue;
          sub.segments.forEach((seg, i) => {
            const nextIndex =
              i + 1 < sub.segments.length ? i + 1 : sub.closed ? 0 : -1;
            if (nextIndex < 0) return;
            const next = sub.segments[nextIndex]!;
            // A curved segment's straight-line midpoint wouldn't lie on the
            // path, so it isn't a target — same test the snap uses.
            if (!seg.handleOut.isZero() || !next.handleIn.isZero()) return;
            out.push({
              x: (seg.point.x + next.point.x) / 2,
              y: (seg.point.y + next.point.y) / 2,
            });
          });
        }
        cp.remove();
      }
    }

    // A closed shape's centre, and each of its 4 bbox edge midpoints. Open
    // non-primitive lines have neither (both helpers return null).
    const center = getBboxCenter(line, scope);
    if (!center) continue;
    out.push(center);
    for (const edge of BBOX_EDGES) {
      const mid = getBboxMidpoint(line, edge, scope);
      if (mid) out.push(mid);
    }
  }
  return out;
}

/**
 * Every discrete snappable point in the project, in DISPLAYED (mm) space,
 * limited to the tiers currently armed. Returns `[]` when nothing is armed.
 */
export function gatherSnapCandidatePoints(
  project: Project,
  scope: paper.PaperScope,
  partLayoutView: "spread" | "assembled",
  active: ActiveSnapKinds,
): Vec2[] {
  if (!active.point && !active.line) return [];
  const targets = gatherDrawSnapTargets(project, scope, partLayoutView);
  const out: Vec2[] = [];
  if (active.point) {
    out.push(...targets.vertices);
    out.push(...constructionExtraPoints(project, scope));
  }
  // Crossings ride the LINE toggle, because a crossing exists only because
  // lines do — the same rule `resolveDrawSnap` applies to the tier itself.
  if (active.line) out.push(...edgeIntersections(targets.edges, scope));
  return dedupe(out);
}
